<template>
    <SubHeader title="Tableau de bord" :breads="[{ name: 'Tableau de bord' }]" :action="{}" />

    <div v-if="isLoading" class=""
        style="min-height: 100%;display: flex;flex-direction: column;align-items: center;justify-content: center;">
        <div class="spinner-grow" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
    </div>

    <div v-else class="container-fluid px-4 py-3">
        <!-- Indicateurs clés -->
        <div class="row mb-4" v-if="data.indicators">
            <div class="col-md-3">
                <div class="card card-body shadow-sm">
                    <div class="d-flex justify-content-between align-items-center">
                        <div>
                            <h6 class="card-subtitle mb-2 text-muted">Ce mois</h6>
                            <h2 class="card-title mb-0 text-primary">{{ data.indicators.total_this_month }}</h2>
                            <small class="text-muted">Nouvelles plaintes</small>
                        </div>
                        <div class="fs-1 text-primary opacity-25">
                            <i class="bi bi-calendar-event"></i>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="card card-body shadow-sm">
                    <div class="d-flex justify-content-between align-items-center">
                        <div>
                            <h6 class="card-subtitle mb-2 text-muted">Cette année</h6>
                            <h2 class="card-title mb-0 text-info">{{ data.indicators.total_this_year }}</h2>
                            <small class="text-muted">Total</small>
                        </div>
                        <div class="fs-1 text-info opacity-25">
                            <i class="bi bi-graph-up"></i>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="card card-body shadow-sm">
                    <div class="d-flex justify-content-between align-items-center">
                        <div>
                            <h6 class="card-subtitle mb-2 text-muted">Taux de complétion</h6>
                            <h2 class="card-title mb-0 text-success">{{ data.indicators.completion_rate }}%</h2>
                            <small class="text-muted">Dossiers clôturés</small>
                        </div>
                        <div class="fs-1 text-success opacity-25">
                            <i class="bi bi-check-circle"></i>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="card card-body shadow-sm">
                    <div class="d-flex justify-content-between align-items-center">
                        <div>
                            <h6 class="card-subtitle mb-2 text-muted">Temps moyen</h6>
                            <h2 class="card-title mb-0 text-warning">{{ data.indicators.avg_treatment_time }}j</h2>
                            <small class="text-muted">Délai de traitement</small>
                        </div>
                        <div class="fs-1 text-warning opacity-25">
                            <i class="bi bi-clock-history"></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Cartes de statistiques -->
        <div class="row mb-4">
            <div class="col-12">
                <h5 class="mb-3">Vue d'ensemble</h5>
            </div>
            <div class="col-sm-6 col-lg-3 mb-3 mb-lg-5" v-for="(stat, i) in data.cards" :key="i">
                <router-link :to="stat.link" class="card card-hover-shadow h-100">
                    <div class="card-body">
                        <h6 class="card-subtitle">{{ stat.title }} <i class="bi bi-robot mx-2" style="font-size: 20px;" v-if="stat.isIA"></i></h6>
                        <div class="row align-items-center gx-2 mb-1">
                            <div class="col-6">
                                <h2 class="card-title text-inherit">{{ stat.count }}</h2>
                            </div>
                        </div>
                        <span class="badge bg-soft-success text-success">
                            <i class="bi-eye"></i> Voir détails
                        </span>
                    </div>
                </router-link>
            </div>
        </div>

        <!-- Graphiques -->
        <div class="row" v-if="data.charts">
            <!-- Évolution mensuelle -->
            <div class="col-lg-6 mb-4">
                <div class="card h-100">
                    <div class="card-header">
                        <h5 class="card-title mb-0">Évolution mensuelle {{ new Date().getFullYear() }}</h5>
                    </div>
                    <div class="card-body" style="min-height: 300px;">
                        <Line :options="chartOptions" :data="{
                            labels: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
                            datasets: [{
                                label: 'Plaintes',
                                data: Object.values(data.charts.monthly || {}),
                                borderColor: '#0a3764',
                                backgroundColor: 'rgba(10, 55, 100, 0.1)',
                                fill: true,
                                tension: 0.4
                            }]
                        }" />
                    </div>
                </div>
            </div>

            <!-- Par catégorie -->
            <div class="col-lg-6 mb-4">
                <div class="card h-100">
                    <div class="card-header">
                        <h5 class="card-title mb-0">Par catégorie</h5>
                    </div>
                    <div class="card-body" style="min-height: 300px;">
                        <Bar :options="{ ...chartOptions, indexAxis: 'y' }" :data="{
                            labels: data.charts.by_category?.map(d => d.category) || [],
                            datasets: [{
                                label: 'Plaintes',
                                data: data.charts.by_category?.map(d => d.count) || [],
                                backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF']
                            }]
                        }" />
                    </div>
                </div>
            </div>

            <!-- Par statut -->
            <div class="col-lg-4 mb-4">
                <div class="card h-100">
                    <div class="card-header">
                        <h5 class="card-title mb-0">Par statut</h5>
                    </div>
                    <div class="card-body" style="min-height: 300px;">
                        <Doughnut :options="chartOptions" :data="{
                            labels: data.charts.by_status?.map(d => d.label) || [],
                            datasets: [{
                                data: data.charts.by_status?.map(d => d.count) || [],
                                backgroundColor: ['#4BC0C0', '#FFCE56', '#FF6384', '#36A2EB']
                            }]
                        }" />
                    </div>
                </div>
            </div>

            <!-- Par priorité -->
            <div class="col-lg-4 mb-4">
                <div class="card h-100">
                    <div class="card-header">
                        <h5 class="card-title mb-0">Par priorité IA</h5>
                    </div>
                    <div class="card-body" style="min-height: 300px;">
                        <Pie :options="chartOptions" :data="{
                            labels: data.charts.by_priority?.map(d => d.label) || [],
                            datasets: [{
                                data: data.charts.by_priority?.map(d => d.count) || [],
                                backgroundColor: ['#DC3545', '#FFC107', '#17A2B8']
                            }]
                        }" />
                    </div>
                </div>
            </div>

            <!-- Top institutions -->
            <div class="col-lg-4 mb-4">
                <div class="card h-100">
                    <div class="card-header">
                        <h5 class="card-title mb-0">Top 10 Institutions</h5>
                    </div>
                    <div class="card-body" style="min-height: 300px;">
                        <Bar :options="{ ...chartOptions, indexAxis: 'y' }" :data="{
                            labels: data.charts.by_institution?.map(d => d.institution) || [],
                            datasets: [{
                                label: 'Plaintes',
                                data: data.charts.by_institution?.map(d => d.count) || [],
                                backgroundColor: '#0a3764'
                            }]
                        }" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import SubHeader from '@/layouts/admin/SubHeader.vue';
import { mapFields } from "vuex-map-fields";
import { mapActions } from "vuex";
import { Line, Bar, Doughnut, Pie } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, PointElement, LineElement, ArcElement } from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, PointElement, LineElement, ArcElement);

export default {
    name: "DossierStatsStatistique",

    data() {
        return {
            data: {
                cards: [],
                indicators: {
                    total_this_month: 0,
                    total_this_year: 0,
                    completion_rate: 0,
                    avg_treatment_time: 0
                },
                charts: {
                    monthly: {},
                    by_category: [],
                    by_status: [],
                    by_institution: [],
                    by_priority: []
                },
                metadata: {}
            },
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false
            }
        }
    },
    components: {
        SubHeader,
        Line,
        Bar,
        Doughnut,
        Pie
    },
    computed: {
        ...mapFields("dossier/stats", {
            isLoading: "isLoading",
            isSuccess: "isSuccess",
            showMessage: "showMessage",
            message: "message",
            items: "items"

        })
    },

    methods: {
        ...mapActions({
            getStatistique: "dossier/stats/getAll",
            reset: "dossier/stats/resetData",
        })
    },

    watch: {
        items: {
            handler(newVal) {
                if (!newVal) {
                    return;
                }

                // newVal contient l'objet {cards, charts, indicators} venant de l'API
                if (newVal.cards && Array.isArray(newVal.cards)) {
                    this.data = newVal;
                }
            },
            immediate: true,
            deep: true
        }
    },

    mounted() {
        this.getStatistique()
    },
    unmounted() {
        this.reset()
    }
}
</script>

<style scoped>
.bg-gradient-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}
.bg-gradient-info {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}
.bg-gradient-success {
    background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}
.bg-gradient-warning {
    background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
}

.card-hover-shadow {
    transition: all 0.3s ease;
}

.card-hover-shadow:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}
</style>